import { Paragraph } from '@movingimage-evp/mi-ui-component-library';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { DownloadReport } from './components/download-report/download-report';
import { EventSummaryHeader } from './components/event-summary-header/event-summary-header';
import { EventSummaryMetrics } from './components/event-summary-metrics/event-summary-metrics';
import { EventSummaryVideo } from './components/event-summary-video/event-summary-video';
import { ReportType, useGetWebcastSummaryManagerQuery } from '../../../../generated/graphql-manager';
import { useGetPrimaryTitle } from '../../../hooks/use-get-primary-title';

import styles from './event-summary.module.css';

export function EventSummary() {
  const { t } = useTranslation();
  const { webcastId = '' } = useParams();
  const title = useGetPrimaryTitle() || '';

  const { data, refetch, startPolling, stopPolling } = useGetWebcastSummaryManagerQuery({
    variables: { webcastId },
    skip: !webcastId,
  });

  const webcast = data?.webcast;
  const session = data?.session;
  const avgPlaytime = webcast?.analytics?.avgPlaytime || 0;

  useEffect(() => {
    if (avgPlaytime < 0) startPolling(60000);
    return () => stopPolling();
  }, [avgPlaytime, startPolling, stopPolling]);

  if (!webcast) return null;

  const wasChatUsed = Boolean(webcast?.chat?.settings.type);
  const wasSecurityUsed = Boolean(webcast?.securityPolicies?.find(({ enabled }) => enabled));

  return (
    <>
      <section className={styles.container} data-testid="event-summary-page">
        <EventSummaryHeader webcast={webcast} webcastTitle={title} session={session} onEventArchiveProcess={refetch} />

        <EventSummaryMetrics webcast={webcast} session={session} />

        <EventSummaryVideo webcast={webcast} onVideoSelect={refetch} />

        <div className={styles.reportsWrapper}>
          <Paragraph>{t('manager.eventSummary.reports.title')}</Paragraph>

          <div className={styles.downloadButtonsWrapper}>
            <DownloadReport
              label={t('manager.eventSummary.reports.downloadViewers')}
              generateLabel={t('manager.eventSummary.reports.generateViewers')}
              infoText={t('manager.eventSummary.reports.infoIconTooltip.viewersReport')}
              webcastId={webcast.id}
              reportType={ReportType.VIEWS_REPORT}
            />

            {wasSecurityUsed && (
              <DownloadReport
                label={t('manager.eventSummary.reports.downloadRegisteredUsers')}
                generateLabel={t('manager.eventSummary.reports.generateRegisteredUsers')}
                infoText={t('manager.eventSummary.reports.infoIconTooltip.attendanceReport')}
                webcastId={webcast.id}
                reportType={ReportType.REGISTERED_USERS_REPORT}
              />
            )}

            {wasChatUsed && (
              <DownloadReport
                label={t('manager.eventSummary.reports.downloadChatMessages')}
                generateLabel={t('manager.eventSummary.reports.generateChatMessages')}
                infoText={t('manager.eventSummary.reports.infoIconTooltip.chatMessagesReport')}
                webcastId={webcast.id}
                reportType={ReportType.CHAT_MESSAGES_REPORT}
              />
            )}
          </div>
        </div>
      </section>
    </>
  );
}
